import type { z } from "zod";
import { apiErrorSchema } from "../../api/contracts";

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly body: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

let csrfToken: string | null = null;
/** Set from /api/me; sent on every mutating request. */
export function setCsrfToken(token: string | null): void { csrfToken = token; }

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function friendlyMessage(status: number, body: unknown): { code: string; message: string } {
  const parsed = apiErrorSchema.safeParse(body);
  const e = parsed.success ? parsed.data : { error: `http_${status}`, message: undefined, ref: undefined, errors: undefined, issues: undefined };
  switch (e.error) {
    case "unauthenticated": return { code: e.error, message: "Your session has ended. Please sign in again." };
    case "csrf": return { code: e.error, message: "Your session token is stale. Reload the page and try again." };
    case "no_access": return { code: e.error, message: e.message ?? "This account is not linked to any locations." };
    case "forbidden": return { code: e.error, message: "You do not have permission to do that." };
    case "not_found": return { code: e.error, message: "That item could not be found." };
    case "invalid_rules": return { code: e.error, message: (e.errors ?? []).join(" ") || "The rules are not valid." };
    case "bad_request": return { code: e.error, message: e.message ?? ((e.issues ?? []).map((i) => `${i.path}: ${i.message}`).join("; ") || "The request was not valid.") };
    case "internal": return { code: e.error, message: `Something went wrong on the server${e.ref ? ` (reference ${e.ref})` : ""}. Try again in a moment.` };
    default: return { code: e.error, message: e.message ?? `Request failed (${status}).` };
  }
}

export interface ApiInit { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: unknown; fetchImpl?: typeof fetch }

/** Fetch + parse. Every response goes through its contract schema so a server drift fails loudly here, not deep in a component. */
export async function api<T>(path: string, schema: z.ZodType<T>, init: ApiInit = {}): Promise<T> {
  const method = init.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (init.body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING.has(method) && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const res = await (init.fetchImpl ?? fetch)(path, { method, headers, credentials: "same-origin", body: init.body === undefined ? undefined : JSON.stringify(init.body) });
  if (res.status === 204) return schema.parse(undefined);
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const f = friendlyMessage(res.status, body);
    throw new ApiError(res.status, f.code, f.message, body);
  }
  return schema.parse(body);
}

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : "";
}
